import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Mobile-optimised login screen. Uses the same /api/auth/login endpoint so
 * any admin/client account works. On success, bounces back to the `from`
 * route (or /m/library by default).
 */
export default function MobileLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/m/library";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-stone-950 text-stone-100 flex flex-col" data-testid="mobile-login-page">
      <div className="pt-16 px-6 text-center">
        <div className="font-display uppercase tracking-wider text-3xl">
          Rodeo <span className="text-orange-400">Cut</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Mobile</div>
      </div>

      <form onSubmit={submit} className="px-6 pt-12 space-y-4 max-w-md mx-auto w-full">
        <div>
          <label className="label-tiny block mb-1">Email</label>
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            className="field-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="mobile-login-email"
          />
        </div>
        <div>
          <label className="label-tiny block mb-1">Password</label>
          <input
            type="password"
            autoComplete="current-password"
            required
            className="field-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="mobile-login-password"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !email || !password}
          className="btn-primary w-full py-3 inline-flex items-center justify-center gap-2"
          data-testid="mobile-login-submit"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Sign in
        </button>
        <div className="text-center text-[10px] text-stone-500 pt-4">
          Prefer the full app? <Link to="/login" className="text-orange-400 underline">Desktop login</Link>
        </div>
      </form>
    </div>
  );
}
